import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Plus,
  Filter,
  Download,
  Printer,
  X,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle
} from 'lucide-react'
import { getColors } from '../../theme/colors'
import {
  fetchUserEntries,
  fetchAllEntries,
  fetchUserCategories,
  fetchSummary,
  fetchEmployees,
  setFilters,
  clearFilters,
  clearError,
  clearSuccessMessage,
  selectCashDayBook
} from '../../store/slices/cashDayBookSlice'
import { dateUtils } from '../../api'

import EntryModal from './components/EntryModal'
import EntriesTable from './components/EntriesTable'
import SummaryCards from './components/SummaryCards'
import FilterPanel from './components/FilterPanel'
import CategoryOverview from './components/CategoryOverview'
import { closeCashBook, resetActiveCategory } from '../../store/slices/cashDayBookSlice'

const CashDayBook = () => {
  const dispatch = useDispatch()
  const {
    entries,
    categories,
    employees,
    summary,
    categoryBreakdown,
    pagination,
    filters,
    loading,
    error,
    successMessage
  } = useSelector(selectCashDayBook)

  const [showEntryModal, setShowEntryModal] = useState(false)
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const [selectedUser, setSelectedUser] = useState('')
  const [closeDate, setCloseDate] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [categoryCloseDate, setCategoryCloseDate] = useState('')
  const [activeTab, setActiveTab] = useState('personal') // personal, all, close-khata, close-category
  const [showExportMenu, setShowExportMenu] = useState(false)
  const [showPrintMenu, setShowPrintMenu] = useState(false)
  const [editingEntry, setEditingEntry] = useState(null)

  // Get user info from auth state
  const user = useSelector(state => state.auth?.user)
  const isAdmin = user?.role === 'Admin'

  // Get current theme colors
  const colors = getColors()

  useEffect(() => {
    if (user) {
      loadInitialData()
    }
  }, [user, activeTab, filters])

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchEmployees())
    }
  }, [isAdmin])

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (showExportMenu && !event.target.closest('.export-dropdown')) {
        setShowExportMenu(false)
      }
      if (showPrintMenu && !event.target.closest('.print-dropdown')) {
        setShowPrintMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showExportMenu, showPrintMenu])

  const loadInitialData = () => {
    if (activeTab === 'personal') {
      dispatch(fetchUserEntries({ ...filters }))
      dispatch(fetchUserCategories())
      dispatch(fetchSummary({ ...filters }))
    } else if (activeTab === 'all' && isAdmin) {
      dispatch(fetchAllEntries({ ...filters }))
    }
  }

  const handleFilterChange = newFilters => {
    dispatch(setFilters(newFilters))
  }

  const handleExportCSV = async () => {
    // Prefer native Excel export if available; fallback to CSV
    try {
      const { default: XLSX } = await import('xlsx')
      const rows = entries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'CashDayBook')
      XLSX.writeFile(wb, `cash-day-book-${Date.now()}.xlsx`)
    } catch (e) {
      const exportData = entries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      dateUtils.exportToCSV(exportData, `cash-day-book-${Date.now()}.csv`)
    }
  }

  const handlePrint = () => {
    window.print()
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
    dispatch(clearFilters())
  }

  const handleCloseKhata = async () => {
    if (!selectedUser) return

    try {
      await dispatch(
        closeCashBook({
          userId: selectedUser,
          endDate: closeDate || null
        })
      ).unwrap()

      setSelectedUser('')
      setCloseDate('')
    } catch (error) {
      // Error handled by slice
    }
  }

  const handleEditEntry = entry => {
    setEditingEntry(entry)
    setShowEntryModal(true)
  }

  const handleCloseEntryModal = () => {
    setShowEntryModal(false)
    setEditingEntry(null)
    dispatch(resetActiveCategory())
  }

  const handleCloseCategoryKhata = async () => {
    if (!selectedCategory) return

    try {
      // This would need to be implemented in the backend
      // For now, we'll show a success message
      // console.log(
      //   'Closing category khata:',
      //   selectedCategory,
      //   categoryCloseDate
      // )
      setSelectedCategory('')
      setCategoryCloseDate('')
    } catch (error) {
      // Error handled by slice
    }
  }

  const handleCategoryExport = async category => {
    try {
      const { default: XLSX } = await import('xlsx')
      const categoryEntries = entries.filter(
        entry => entry.category === category
      )
      const rows = categoryEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, category)
      XLSX.writeFile(wb, `${category}-cash-day-book-${Date.now()}.xlsx`)
    } catch (e) {
      const categoryEntries = entries.filter(
        entry => entry.category === category
      )
      const exportData = categoryEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      dateUtils.exportToCSV(
        exportData,
        `${category}-cash-day-book-${Date.now()}.csv`
      )
    }
  }

  const handleCategoryPrint = category => {
    const categoryEntries = entries.filter(entry => entry.category === category)
    const printContent = `
      <html>
        <head>
          <title>Cash Day Book - ${category}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .income { color: green; }
            .expense { color: red; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Cash Day Book Report</h1>
            <h2>Category: ${category}</h2>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h3>Summary for ${category}</h3>
            <p><strong>Total Income:</strong> <span class="income">Rs ${categoryBreakdown.find(c => c._id === category)?.totalIncome || 0
      }</span></p>
            <p><strong>Total Expense:</strong> <span class="expense">Rs ${categoryBreakdown.find(c => c._id === category)?.totalExpense || 0
      }</span></p>
            <p><strong>Net Balance:</strong> <span class="${(categoryBreakdown.find(c => c._id === category)?.netBalance ||
        0) >= 0
        ? 'income'
        : 'expense'
      }">Rs ${categoryBreakdown.find(c => c._id === category)?.netBalance || 0
      }</span></p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              ${categoryEntries
        .map(
          entry => `
                <tr>
                  <td>${dateUtils.formatDate(entry.date)}</td>
                  <td class="${entry.type.toLowerCase()}">${entry.type}</td>
                  <td>Rs ${entry.amount}</td>
                  <td>${entry.description || ''}</td>
                  <td>${entry.user?.username || ''}</td>
                </tr>
              `
        )
        .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const printWindow = window.open('', '_blank')
    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.print()
  }

  const handleMonthlyExport = async (month, year) => {
    try {
      const { default: XLSX } = await import('xlsx')
      const monthlyEntries = entries.filter(entry => {
        const entryDate = new Date(entry.date)
        return (
          entryDate.getMonth() === month && entryDate.getFullYear() === year
        )
      })
      const rows = monthlyEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, `${month + 1}-${year}`)
      XLSX.writeFile(
        wb,
        `cash-day-book-${month + 1}-${year}-${Date.now()}.xlsx`
      )
    } catch (e) {
      const monthlyEntries = entries.filter(entry => {
        const entryDate = new Date(entry.date)
        return (
          entryDate.getMonth() === month && entryDate.getFullYear() === year
        )
      })
      const exportData = monthlyEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      dateUtils.exportToCSV(
        exportData,
        `cash-day-book-${month + 1}-${year}-${Date.now()}.csv`
      )
    }
  }

  const handleYearlyExport = async year => {
    try {
      const { default: XLSX } = await import('xlsx')
      const yearlyEntries = entries.filter(entry => {
        const entryDate = new Date(entry.date)
        return entryDate.getFullYear() === year
      })
      const rows = yearlyEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, year.toString())
      XLSX.writeFile(wb, `cash-day-book-${year}-${Date.now()}.xlsx`)
    } catch (e) {
      const yearlyEntries = entries.filter(entry => {
        const entryDate = new Date(entry.date)
        return entryDate.getFullYear() === year
      })
      const exportData = yearlyEntries.map(entry => ({
        Date: dateUtils.formatDate(entry.date),
        Category: entry.category,
        Type: entry.type,
        Amount: entry.amount,
        Description: entry.description || '',
        'Created By': entry.createdBy?.username || '',
        User: entry.user?.username || ''
      }))
      dateUtils.exportToCSV(
        exportData,
        `cash-day-book-${year}-${Date.now()}.csv`
      )
    }
  }

  const handleMonthlyPrint = (month, year) => {
    const monthlyEntries = entries.filter(entry => {
      const entryDate = new Date(entry.date)
      return entryDate.getMonth() === month && entryDate.getFullYear() === year
    })
    const monthName = new Date(year, month).toLocaleString('default', {
      month: 'long'
    })
    const printContent = `
      <html>
        <head>
          <title>Cash Day Book - ${monthName} ${year}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .income { color: green; }
            .expense { color: red; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Cash Day Book Report</h1>
            <h2>${monthName} ${year}</h2>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h3>Summary for ${monthName} ${year}</h3>
            <p><strong>Total Entries:</strong> ${monthlyEntries.length}</p>
            <p><strong>Total Income:</strong> <span class="income">Rs ${monthlyEntries
        .filter(e => e.type === 'Income')
        .reduce((sum, e) => sum + e.amount, 0)}</span></p>
            <p><strong>Total Expense:</strong> <span class="expense">Rs ${monthlyEntries
        .filter(e => e.type === 'Expense')
        .reduce((sum, e) => sum + e.amount, 0)}</span></p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              ${monthlyEntries
        .map(
          entry => `
                <tr>
                  <td>${dateUtils.formatDate(entry.date)}</td>
                  <td>${entry.category}</td>
                  <td class="${entry.type.toLowerCase()}">${entry.type}</td>
                  <td>Rs ${entry.amount}</td>
                  <td>${entry.description || ''}</td>
                  <td>${entry.user?.username || ''}</td>
                </tr>
              `
        )
        .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const printWindow = window.open('', '_blank')
    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.print()
  }

  const handleYearlyPrint = year => {
    const yearlyEntries = entries.filter(entry => {
      const entryDate = new Date(entry.date)
      return entryDate.getFullYear() === year
    })
    const printContent = `
      <html>
        <head>
          <title>Cash Day Book - ${year}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .summary { margin-bottom: 20px; padding: 15px; background: #f5f5f5; border-radius: 5px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
            .income { color: green; }
            .expense { color: red; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Cash Day Book Report</h1>
            <h2>Year ${year}</h2>
            <p>Generated on: ${new Date().toLocaleDateString()}</p>
          </div>
          
          <div class="summary">
            <h3>Summary for ${year}</h3>
            <p><strong>Total Entries:</strong> ${yearlyEntries.length}</p>
            <p><strong>Total Income:</strong> <span class="income">Rs ${yearlyEntries
        .filter(e => e.type === 'Income')
        .reduce((sum, e) => sum + e.amount, 0)}</span></p>
            <p><strong>Total Expense:</strong> <span class="expense">Rs ${yearlyEntries
        .filter(e => e.type === 'Expense')
        .reduce((sum, e) => sum + e.amount, 0)}</span></p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              ${yearlyEntries
        .map(
          entry => `
                <tr>
                  <td>${dateUtils.formatDate(entry.date)}</td>
                  <td>${entry.category}</td>
                  <td class="${entry.type.toLowerCase()}">${entry.type}</td>
                  <td>Rs ${entry.amount}</td>
                  <td>${entry.description || ''}</td>
                  <td>${entry.user?.username || ''}</td>
                </tr>
              `
        )
        .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const printWindow = window.open('', '_blank')
    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.print()
  }

  return (
    <div
      className={`min-h-screen bg-gradient-to-br ${colors.bgGradientFrom} ${colors.bgGradientVia} ${colors.bgGradientTo}`}
    >
      <div className='container mx-auto px-4 py-8'>
        {/* Header */}
        <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-8'>
          <div>
            <h1 className={`text-3xl font-bold ${colors.textPrimary} mb-2`}>
              Cash Day Book (Khata System)
            </h1>
            <p className={`${colors.textSecondary}`}>
              Manage your daily income and expenses
            </p>
          </div>

          <div className='flex flex-wrap gap-3 mt-4 md:mt-0'>
            <button
              onClick={() => {
                setEditingEntry(null)
                setShowEntryModal(true)
              }}
              className={`bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2`}
            >
              <Plus size={20} />
              Add Entry
            </button>

            <button
              onClick={() => setShowFilterPanel(!showFilterPanel)}
              className={`bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2`}
            >
              <Filter size={20} />
              Filters
            </button>

            {/* Export Dropdown */}
            <div className='relative export-dropdown'>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className={`bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2`}
                disabled={!entries.length}
              >
                <Download size={20} />
                Export
              </button>

              {showExportMenu && (
                <div
                  className={`absolute right-0 mt-2 w-64 ${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-lg shadow-lg z-50`}
                >
                  <div className='p-2'>
                    <button
                      onClick={() => {
                        handleExportCSV()
                        setShowExportMenu(false)
                      }}
                      className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                    >
                      Export All Data
                    </button>
                    <div className='border-t border-gray-600 my-1'></div>
                    <div className='px-3 py-1 text-xs text-gray-400'>
                      Export by Category:
                    </div>
                    {categoryBreakdown.map(category => (
                      <button
                        key={category._id}
                        onClick={() => {
                          handleCategoryExport(category._id)
                          setShowExportMenu(false)
                        }}
                        className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                      >
                        {category._id} (
                        {category.incomeCount + category.expenseCount} entries)
                      </button>
                    ))}
                    <div className='border-t border-gray-600 my-1'></div>
                    <div className='px-3 py-1 text-xs text-gray-400'>
                      Export by Time Period:
                    </div>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = new Date(2024, i).toLocaleString(
                        'default',
                        { month: 'long' }
                      )
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            handleMonthlyExport(i, new Date().getFullYear())
                            setShowExportMenu(false)
                          }}
                          className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                        >
                          {month} {new Date().getFullYear()}
                        </button>
                      )
                    })}
                    <div className='border-t border-gray-600 my-1'></div>
                    <button
                      onClick={() => {
                        handleYearlyExport(new Date().getFullYear())
                        setShowExportMenu(false)
                      }}
                      className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                    >
                      Full Year {new Date().getFullYear()}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Print Dropdown */}
            <div className='relative print-dropdown'>
              <button
                onClick={() => setShowPrintMenu(!showPrintMenu)}
                className={`bg-gradient-to-r ${colors.btnSecondaryFrom} ${colors.btnSecondaryTo} ${colors.textPrimary} px-6 py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center gap-2`}
              >
                <Printer size={20} />
                Print
              </button>

              {showPrintMenu && (
                <div
                  className={`absolute right-0 mt-2 w-64 ${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-lg shadow-lg z-50`}
                >
                  <div className='p-2'>
                    <button
                      onClick={() => {
                        handlePrint()
                        setShowPrintMenu(false)
                      }}
                      className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                    >
                      Print All Data
                    </button>
                    <div className='border-t border-gray-600 my-1'></div>
                    <div className='px-3 py-1 text-xs text-gray-400'>
                      Print by Category:
                    </div>
                    {categoryBreakdown.map(category => (
                      <button
                        key={category._id}
                        onClick={() => {
                          handleCategoryPrint(category._id)
                          setShowPrintMenu(false)
                        }}
                        className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                      >
                        {category._id} (
                        {category.incomeCount + category.expenseCount} entries)
                      </button>
                    ))}
                    <div className='border-t border-gray-600 my-1'></div>
                    <div className='px-3 py-1 text-xs text-gray-400'>
                      Print by Time Period:
                    </div>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = new Date(2024, i).toLocaleString(
                        'default',
                        { month: 'long' }
                      )
                      return (
                        <button
                          key={i}
                          onClick={() => {
                            handleMonthlyPrint(i, new Date().getFullYear())
                            setShowPrintMenu(false)
                          }}
                          className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                        >
                          {month} {new Date().getFullYear()}
                        </button>
                      )
                    })}
                    <div className='border-t border-gray-600 my-1'></div>
                    <button
                      onClick={() => {
                        handleYearlyPrint(new Date().getFullYear())
                        setShowPrintMenu(false)
                      }}
                      className={`w-full text-left px-3 py-2 ${colors.textPrimary} hover:${colors.cardBgHover} rounded transition-colors`}
                    >
                      Full Year {new Date().getFullYear()}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className='bg-red-500 bg-opacity-20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 flex justify-between items-center'>
            <span>{error}</span>
            <button
              onClick={() => dispatch(clearError())}
              className='text-red-300 hover:text-red-100'
            >
              <X size={20} />
            </button>
          </div>
        )}

        {successMessage && (
          <div className='bg-green-500 bg-opacity-20 border border-green-500 text-green-300 px-4 py-3 rounded-lg mb-6 flex justify-between items-center'>
            <span>{successMessage}</span>
            <button
              onClick={() => dispatch(clearSuccessMessage())}
              className='text-green-300 hover:text-green-100'
            >
              <X size={20} />
            </button>
          </div>
        )}

        {/* Tabs (Admin Only) */}
        {isAdmin && (
          <div className='flex space-x-1 mb-6'>
            <button
              onClick={() => handleTabChange('personal')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${activeTab === 'personal'
                  ? `bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary}`
                  : `${colors.cardBg} ${colors.cardBgOpacity} ${colors.textSecondary} hover:${colors.cardBgHover}`
                }`}
            >
              My Khata
            </button>
            <button
              onClick={() => handleTabChange('all')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${activeTab === 'all'
                  ? `bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary}`
                  : `${colors.cardBg} ${colors.cardBgOpacity} ${colors.textSecondary} hover:${colors.cardBgHover}`
                }`}
            >
              All Employees
            </button>
            <button
              onClick={() => handleTabChange('close-khata')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${activeTab === 'close-khata'
                  ? `bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary}`
                  : `${colors.cardBg} ${colors.cardBgOpacity} ${colors.textSecondary} hover:${colors.cardBgHover}`
                }`}
            >
              Close Khata
            </button>
            <button
              onClick={() => handleTabChange('close-category')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${activeTab === 'close-category'
                  ? `bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary}`
                  : `${colors.cardBg} ${colors.cardBgOpacity} ${colors.textSecondary} hover:${colors.cardBgHover}`
                }`}
            >
              Close Category
            </button>
          </div>
        )}

        {/* Filter Panel */}
        {showFilterPanel && (
          <FilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClose={() => setShowFilterPanel(false)}
            categories={categories}
            employees={employees}
            isAdmin={isAdmin}
            activeTab={activeTab}
          />
        )}

        {/* Summary Section */}
        <SummaryCards
          summary={summary}
          categoryBreakdown={categoryBreakdown}
          loading={loading.fetchingSummary}
        />

        {/* Category Grid Section */}
        {activeTab === 'personal' && (
          <CategoryOverview
            categoryBreakdown={categoryBreakdown}
            onAddEntry={() => setShowEntryModal(true)}
            loading={loading.fetchingSummary}
          />
        )}

        {/* Close Khata Tab Content */}
        {activeTab === 'close-khata' && isAdmin && (
          <div
            className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6`}
          >
            <h3
              className={`${colors.textPrimary} text-xl font-semibold mb-4 flex items-center gap-2`}
            >
              <AlertCircle className={colors.textHighlight} size={24} />
              Close Khata for Users
            </h3>
            <p className={`${colors.textSecondary} mb-6`}>
              Select a user and date to close their cash book. This will
              finalize all entries up to the specified date.
            </p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label
                  className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
                >
                  Select User
                </label>
                <select
                  value={selectedUser}
                  onChange={e => setSelectedUser(e.target.value)}
                  className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
                >
                  <option value=''>Select a user to close their Khata</option>
                  {employees.map(employee => (
                    <option key={employee._id} value={employee._id}>
                      {employee.username} -{' '}
                      {employee.employeeData?.designation || employee.role}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
                >
                  Close Date (Optional)
                </label>
                <input
                  type='date'
                  value={closeDate}
                  onChange={e => setCloseDate(e.target.value)}
                  className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
                />
                <p className={`${colors.textMuted} text-xs mt-1`}>
                  Leave empty to close all open entries
                </p>
              </div>
            </div>

            <div className='mt-6 flex gap-3'>
              <button
                onClick={handleCloseKhata}
                disabled={!selectedUser || loading.closing}
                className={`bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              >
                {loading.closing ? 'Closing...' : 'Close Khata'}
              </button>
            </div>
          </div>
        )}

        {/* Close Category Tab Content */}
        {activeTab === 'close-category' && isAdmin && (
          <div
            className={`${colors.cardBg} ${colors.cardBgOpacity} border ${colors.cardBorder} rounded-xl p-6`}
          >
            <h3
              className={`${colors.textPrimary} text-xl font-semibold mb-4 flex items-center gap-2`}
            >
              <AlertCircle className={colors.textHighlight} size={24} />
              Close Category Khata
            </h3>
            <p className={`${colors.textSecondary} mb-6`}>
              Select a category and date to close all entries for that category.
              This will finalize all category entries up to the specified date.
            </p>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              <div>
                <label
                  className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
                >
                  Select Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={e => setSelectedCategory(e.target.value)}
                  className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
                >
                  <option value=''>Select a category to close</option>
                  {categoryBreakdown.map(category => (
                    <option key={category._id} value={category._id}>
                      {category._id} (
                      {category.incomeCount + category.expenseCount} entries)
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block ${colors.textPrimary} text-sm font-medium mb-2`}
                >
                  Close Date (Optional)
                </label>
                <input
                  type='date'
                  value={categoryCloseDate}
                  onChange={e => setCategoryCloseDate(e.target.value)}
                  className={`w-full p-3 ${colors.inputBg} border ${colors.inputBorder} rounded-lg ${colors.textPrimary} ${colors.inputFocusBorder} focus:ring-2 ${colors.inputFocusRing} focus:ring-opacity-50 transition-all`}
                />
                <p className={`${colors.textMuted} text-xs mt-1`}>
                  Leave empty to close all category entries
                </p>
              </div>
            </div>

            <div className='mt-6 flex gap-3'>
              <button
                onClick={handleCloseCategoryKhata}
                disabled={!selectedCategory || loading.closing}
                className={`bg-gradient-to-r ${colors.btnPrimaryFrom} ${colors.btnPrimaryTo} ${colors.textPrimary} px-6 py-3 rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
              >
                {loading.closing ? 'Closing...' : 'Close Category'}
              </button>
            </div>
          </div>
        )}

        {/* Entries Table */}
        {activeTab !== 'close-khata' && activeTab !== 'close-category' && (
          <EntriesTable
            entries={entries}
            loading={loading.entries}
            pagination={pagination}
            isAdmin={isAdmin}
            activeTab={activeTab}
            onEditEntry={handleEditEntry}
            onLoadMore={() => {
              const nextPage = pagination.current + 1
              if (activeTab === 'personal') {
                dispatch(fetchUserEntries({ ...filters, page: nextPage }))
              } else {
                dispatch(fetchAllEntries({ ...filters, page: nextPage }))
              }
            }}
          />
        )}

        {/* Entry Modal */}
        {showEntryModal && (
          <EntryModal
            onClose={handleCloseEntryModal}
            categories={categories}
            employees={employees}
            isAdmin={isAdmin}
            editingEntry={editingEntry}
          />
        )}
      </div>
    </div>
  )
}

export default CashDayBook
